import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  UpcomingEventsState,
  Holiday,
  LocalEvent,
  EventMaster,
  CombinedEvent,
} from '../../components/upcomingEvents/types';

const initialState: UpcomingEventsState = {
  holidays: [],
  localEvents: [],
  eventMaster: [],
  combinedEvents: [],
  loading: false,
  error: null,
  countryCode: 'US', // Default country code
};

const upcomingEventsSlice = createSlice({
  name: 'upcomingEvents',
  initialState,
  reducers: {
    // Fetch upcoming events (triggers saga to fetch all 3 APIs)
    fetchUpcomingEventsRequest(state, action: PayloadAction<{ restaurantId: string; countryCode: string }>) {
      state.loading = true;
      state.error = null;
      state.countryCode = action.payload.countryCode;
    },
    
    fetchUpcomingEventsSuccess(
      state,
      action: PayloadAction<{
        holidays: Holiday[];
        localEvents: LocalEvent[];
        eventMaster: EventMaster[];
      }>
    ) {
      state.holidays = action.payload.holidays;
      state.localEvents = action.payload.localEvents;
      state.eventMaster = action.payload.eventMaster;
      state.loading = false;
      state.error = null;
      
      // Combine all events
      state.combinedEvents = combineEvents(
        action.payload.holidays,
        action.payload.localEvents,
        action.payload.eventMaster
      );
    },
    
    fetchUpcomingEventsFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    
    setCountryCode(state, action: PayloadAction<string>) {
      state.countryCode = action.payload;
    },
  },
});

// Helper function to combine events
function combineEvents(
  holidays: Holiday[],
  localEvents: LocalEvent[],
  eventMaster: EventMaster[]
): CombinedEvent[] {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  
  // Process holidays
  const processedHolidays: CombinedEvent[] = holidays
    .filter(h => {
      const holidayDate = new Date(h.holiday_date);
      return holidayDate.getFullYear() === currentYear;
    })
    .map(holiday => ({
      id: `holiday-${holiday.holiday_id}`,
      name: holiday.holiday_name,
      date: holiday.holiday_date,
      country_code: holiday.country_code,
      type: holiday.holiday_type,
      category: 'Holiday' as const,
      consumer_behavior: holiday.consumer_behavior,
      marketing_angle: holiday.marketing_angle,
    }));
  
  // Process local events
  const processedLocalEvents: CombinedEvent[] = localEvents
    .filter(e => {
      const eventDate = new Date(e.event_date);
      return eventDate.getFullYear() === currentYear;
    })
    .map(event => ({
      id: `local-event-${event.id}`,
      name: event.event_name,
      date: event.event_date,
      type: event.event_category,
      category: 'Local Event' as const,
      venue: event.venue,
      location: `${event.city}, ${event.state}`,
      state: event.state,
    }));
  
  // Process event master
  const processedEventMaster: CombinedEvent[] = eventMaster
    .filter(e => {
      const eventDate = new Date(e.start_date);
      return eventDate.getFullYear() === currentYear;
    })
    .map(event => ({
      id: `event-master-${event.id}`,
      name: event.event_name,
      date: event.start_date,
      type: event.sub_category,
      category: 'Event Master' as const,
      location: event.location,
      state: event.state,
      source: event.source,
    }));
  
  // Combine and remove duplicates based on name and date
  const combined = [...processedHolidays, ...processedLocalEvents, ...processedEventMaster];
  
  // Remove duplicates
  const uniqueEvents = combined.filter((event, index, self) =>
    index === self.findIndex((e) => 
      e.name.toLowerCase() === event.name.toLowerCase() && e.date === event.date
    )
  );
  
  // Sort by date
  return uniqueEvents.sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );
}

export const {
  fetchUpcomingEventsRequest,
  fetchUpcomingEventsSuccess,
  fetchUpcomingEventsFailure,
  setCountryCode,
} = upcomingEventsSlice.actions;

export default upcomingEventsSlice.reducer;
